import { useEffect, useState } from "react"
import { Badge, Box, HStack, Image, SimpleGrid, Spacer, Stack } from "@chakra-ui/react"

import { getSecondaryDeals, SecondaryDeal, SecondaryLeg } from "@/api"
import { getPaymentsClientByEmail, listPaymentsBankAccounts } from "@/api/payments"
import { Text } from "@/components"
import { useAuth } from "@/contexts"
import { useClientContext } from "@/hooks"
import { UsPlaidWireInformationComponent } from "@/features/compliance/components/UsPlaidWireInformationComponent"
import secondaryFundLogo from "@/assets/secondary-fund-logo.svg"

// Secondary Fund payment center — the ONLY surface secondary clients get,
// and deliberately styled apart from the Options Fund portal: deep-navy
// hero band with the Secondary Fund wordmark, navy-accented deal cards,
// and per-leg payment progress (Total / Sent / Remaining) instead of the
// options center's pending-amount flow.
//
// Deals come from the Airtable mirror via GET /secondary/deals. An
// Exercise-Into-Purchase deal shows its exercise + liquidity legs; every
// other deal type shows a single purchase leg — the exercise row simply
// doesn't exist for deals that don't require exercise. Payments are staged
// by ESO with deliberate amounts against a verified total; the client's job
// here is to see status and keep a bank account connected.

const NAVY = "#0D4D94"      // Secondary Fund wordmark blue
const NAVY_DARK = "#082F5C"

const formatCurrency = (amount: number | null | undefined) =>
  amount == null
    ? "—"
    : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount)

const formatDate = (iso: string | null) => {
  if (!iso) return null
  const d = new Date(iso)
  return isNaN(d.getTime()) ? null : d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" })
}

const LEG_LABELS: Record<string, string> = {
  exercise: "Exercise Payment",
  liquidity: "Liquidity Payment",
  purchase: "Purchase Payment",
}

const legStatus = (leg: SecondaryLeg): { label: string; palette: string } => {
  if (leg.paid) return { label: "Paid", palette: "green" }
  if (leg.sentAmount > 0 && leg.remaining != null && leg.remaining <= 0.005) return { label: "In Progress", palette: "blue" }
  if (leg.sentAmount > 0) return { label: "Partially Sent", palette: "blue" }
  return { label: "Scheduled", palette: "gray" }
}

const LegRow = ({ legKey, leg }: { legKey: string; leg: SecondaryLeg }) => {
  const status = legStatus(leg)
  const total = leg.totalSet ?? leg.amount
  const pct = leg.paid ? 100 : total && total > 0 ? Math.min(100, Math.round((leg.sentAmount / total) * 100)) : 0
  return (
    <Box py={3} borderBottomWidth="1px" borderColor="gray.100">
      <HStack mb={1}>
        <Text fontSize="sm" fontWeight="semibold" color={NAVY_DARK}>{LEG_LABELS[legKey] ?? legKey}</Text>
        <Spacer />
        <Text fontSize="sm" fontWeight="bold">{formatCurrency(total)}</Text>
        <Badge colorPalette={status.palette} size="sm">{status.label}{leg.paid && formatDate(leg.paidDate) ? ` ${formatDate(leg.paidDate)}` : ""}</Badge>
      </HStack>
      {/* Payment progress — sent vs the verified total */}
      <Box bg="gray.100" borderRadius="full" h="6px" overflow="hidden">
        <Box bg={leg.paid ? "green.500" : NAVY} h="100%" w={`${leg.paid ? 100 : pct}%`} borderRadius="full" transition="width 0.3s" />
      </Box>
      {!leg.paid && (
        <HStack mt={1} gap={4}>
          <Text fontSize="xs" color="gray.500">Sent {formatCurrency(leg.sentAmount)}</Text>
          {leg.remaining != null ? <Text fontSize="xs" color="gray.500">Remaining {formatCurrency(leg.remaining)}</Text> : null}
        </HStack>
      )}
    </Box>
  )
}

const DealCard = ({ deal }: { deal: SecondaryDeal }) => (
  <Stack
    borderWidth="1px"
    borderColor="gray.200"
    borderLeftWidth="4px"
    borderLeftColor={NAVY}
    borderRadius={10}
    p={5}
    gap={1}
    bg="white"
    boxShadow="0 1px 3px rgba(8,47,92,0.08)"
  >
    <HStack>
      <Text fontSize="lg" fontWeight="bold" color={NAVY_DARK}>{deal.company ?? deal.dealId}</Text>
      <Spacer />
      {deal.dealType ? <Badge variant="outline" colorPalette="blue" size="sm">{deal.dealType}</Badge> : null}
    </HStack>
    <HStack gap={4}>
      {formatDate(deal.closingDate) ? <Text fontSize="xs" color="gray.500">Closed {formatDate(deal.closingDate)}</Text> : null}
      {deal.shareCount != null ? <Text fontSize="xs" color="gray.500">{deal.shareCount.toLocaleString("en-US")} shares</Text> : null}
      {deal.pps != null ? <Text fontSize="xs" color="gray.500">{formatCurrency(deal.pps)}/share</Text> : null}
    </HStack>
    <HStack pt={3} pb={1}>
      <Text fontSize="sm" color="gray.600">Total Deal Size</Text>
      <Spacer />
      <Text fontSize="md" fontWeight="bold" color={NAVY_DARK}>{formatCurrency(deal.totalDealSize)}</Text>
    </HStack>
    <Box>
      {Object.entries(deal.legs).map(([key, leg]) => (
        <LegRow key={key} legKey={key} leg={leg} />
      ))}
    </Box>
  </Stack>
)

export const SecondaryComponent = () => {
  const { user } = useAuth()
  // Canonical identity accessor — returns the VIEWED client when a DM is
  // impersonating (View-As), the logged-in client otherwise.
  const clientContext = useClientContext()
  const [deals, setDeals] = useState<SecondaryDeal[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState("")
  const [hasConnectedBank, setHasConnectedBank] = useState<boolean | null>(null)

  const email = clientContext?.email
  const isViewingAsClient = clientContext?.isViewingAsClient === true

  useEffect(() => {
    if (!email) return
    let cancelled = false
    setIsLoading(true)
    // DM View-As reads the viewed client's deals via ?email= (DM-only server-side).
    getSecondaryDeals(isViewingAsClient ? email : undefined)
      .then((res) => { if (!cancelled) { setDeals(res.deals); setLoadError("") } })
      .catch(() => { if (!cancelled) setLoadError("Could not load your Secondary Fund deals. Please try again shortly.") })
      .finally(() => { if (!cancelled) setIsLoading(false) })

    getPaymentsClientByEmail(email)
      .then((paymentsClient) => {
        if (!paymentsClient) { if (!cancelled) setHasConnectedBank(false); return }
        return listPaymentsBankAccounts(paymentsClient.id)
          .then((accounts) => { if (!cancelled) setHasConnectedBank(accounts.length > 0) })
      })
      .catch(() => { if (!cancelled) setHasConnectedBank(false) })
    return () => { cancelled = true }
  }, [email, isViewingAsClient])

  if (!user || !clientContext) return null

  return (
    <Stack gap={0}>
      {/* Hero band — the Secondary Fund's own identity, not the options portal's */}
      <Box bg={`linear-gradient(120deg, ${NAVY_DARK}, ${NAVY})`} px={{ base: 4, md: 8 }} py={6}>
        <HStack gap={4} flexWrap="wrap">
          <Box bg="white" borderRadius={8} px={4} py={2}>
            <Image src={secondaryFundLogo} alt="ESO Secondary Fund" maxH="44px" maxW="260px" objectFit="contain" />
          </Box>
          <Stack gap={0}>
            <Text fontSize="lg" fontWeight="bold" color="white">Payment Center</Text>
            <Text fontSize="xs" color="whiteAlpha.800">Direct share purchases, exercise funding, and liquidity payments</Text>
          </Stack>
        </HStack>
      </Box>

      <Stack p={{ base: 4, md: 8 }} gap={5} maxW="1000px">
        {isLoading ? (
          <Text fontSize="sm" color="gray.500">Loading your transactions…</Text>
        ) : loadError ? (
          <Text fontSize="sm" color="red.500">{loadError}</Text>
        ) : deals.length === 0 ? (
          <Text fontSize="sm" color="gray.500">
            No Secondary Fund transactions found for your account. If you believe this is an error,
            contact your ESO representative.
          </Text>
        ) : (
          <SimpleGrid columns={{ base: 1, lg: 2 }} gap={4}>
            {deals.map((deal) => <DealCard key={deal.dealId} deal={deal} />)}
          </SimpleGrid>
        )}

        {hasConnectedBank === false && !isViewingAsClient && (
          <Stack borderWidth="1px" borderColor="orange.200" bg="orange.50" borderRadius={10} p={5} gap={3}>
            <Text fontSize="md" fontWeight="bold" color={NAVY_DARK}>Connect Your Bank Account</Text>
            <Text fontSize="sm" color="gray.600">
              To receive your Secondary Fund payment{deals.length === 1 ? "" : "s"}, securely connect
              the bank account where funds should be sent.
            </Text>
            <UsPlaidWireInformationComponent
              defaultClientData={{
                firstName: clientContext.firstName ?? "",
                lastName: clientContext.lastName ?? "",
                email: email ?? "",
                companyName: deals[0]?.company ?? "",
                phoneNumber: clientContext.phoneNumber ?? undefined,
              }}
              paymentAmount={0}
              paymentLabel="Connect bank account"
              onSuccess={() => setHasConnectedBank(true)}
            />
          </Stack>
        )}
        {hasConnectedBank === true && (
          <Text fontSize="xs" color="green.600">✓ Bank account connected — payments will be sent to your account on file.</Text>
        )}

        <Text fontSize="2xs" color="gray.400">
          ESO Secondary Fund transactions are administered by ESO Fund. Payments are initiated by ESO
          against verified transaction amounts and released after internal approval.
        </Text>
      </Stack>
    </Stack>
  )
}
